import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { AlertCircle, Check, Eye, EyeOff } from "lucide-react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  PasswordIdentity,
  PasswordStrength,
  passwordChecklist,
  passwordStrength,
} from "@/lib/auth/password";

const TEST_SITE_KEY = "10000000-ffff-ffff-ffff-000000000001";

export function AuthField({
  id,
  label,
  error,
  trailing,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        {trailing}
      </div>
      {children}
      {error && (
        <p id={`${id}-error`} role="alert" className="flex items-start gap-1.5 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

const STRENGTH_LABEL: Record<PasswordStrength, string> = {
  0: "",
  1: "weak",
  2: "fair",
  3: "good",
  4: "strong",
};

export function PasswordField({
  id,
  label,
  value,
  onChange,
  onBlur,
  autoComplete,
  error,
  touched,
  identity,
  trailing,
  describedBy,
  showRules = true,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  autoComplete: string;
  error?: string;
  touched?: boolean;
  identity?: PasswordIdentity;
  trailing?: ReactNode;
  describedBy?: string;
  showRules?: boolean;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const score = passwordStrength(value);
  const checks = passwordChecklist(value, identity);
  const meterId = `${id}-strength`;
  const listId = `${id}-rules`;
  const described = [describedBy, error ? `${id}-error` : null, showRules && value ? meterId : null, showRules ? listId : null].filter(Boolean).join(" ");
  const segmentClass = (index: number) => {
    if (score < index) return "bg-muted";
    if (score <= 1) return "bg-destructive";
    if (score === 2) return "bg-[var(--status-warning-solid)]";
    return "bg-[var(--status-success-solid)]";
  };

  return (
    <AuthField id={id} label={label} error={touched ? error : undefined} trailing={trailing}>
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          autoComplete={autoComplete}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          aria-invalid={Boolean(touched && error)}
          aria-describedby={described || undefined}
          className="h-12 pr-12 text-base"
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex h-12 w-11 items-center justify-center rounded-r-md text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-pressed={visible}
          aria-label={visible ? t("authUi.hidePassword") : t("authUi.showPassword")}
          onClick={() => setVisible((current) => !current)}
        >
          {visible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {showRules && value.length > 0 && (
        <div className="space-y-1 pt-1">
          <div
            id={meterId}
            role="meter"
            aria-label={t("authUi.strength")}
            aria-valuemin={0}
            aria-valuemax={4}
            aria-valuenow={score}
            aria-valuetext={t(`authUi.${STRENGTH_LABEL[score]}`)}
            className="grid grid-cols-4 gap-1"
          >
            {[1, 2, 3, 4].map((index) => (
              <span key={index} className={cn("h-1.5 rounded-full motion-safe:transition-colors motion-safe:duration-200", segmentClass(index))} />
            ))}
          </div>
          <p className="text-sm text-foreground">{t(`authUi.${STRENGTH_LABEL[score]}`)}</p>
        </div>
      )}
      {showRules && (
      <ul id={listId} className="space-y-1 pt-1 text-sm text-muted-foreground">
        {(
          [
            ["length", "ruleLength"],
            ["identity", "ruleIdentity"],
            ["common", "ruleCommon"],
          ] as const
        ).map(([key, labelKey]) => {
          const passed = checks[key];
          const showMiss = touched && !passed;
          return (
            <li key={key} className={cn("flex items-center gap-2", showMiss && "text-destructive")}>
              {passed ? <Check className="h-4 w-4 text-[var(--status-success)]" aria-hidden /> : <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
              {t(`authUi.${labelKey}`)}
            </li>
          );
        })}
      </ul>
      )}
    </AuthField>
  );
}

export type CaptchaHandle = {
  reset: () => void;
  focus: () => void;
};

export function CaptchaSlot({
  enabled,
  siteKey,
  onToken,
  error,
  handleRef,
}: {
  enabled: boolean;
  siteKey: string;
  onToken: (token: string | null) => void;
  error?: string | null;
  handleRef?: React.MutableRefObject<CaptchaHandle | null>;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const [compact, setCompact] = useState(true);
  const { resolvedTheme } = useTheme();
  const { i18n } = useTranslation();
  const labelId = useId();

  useEffect(() => {
    const node = boxRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      setCompact(entry.contentRect.width < 340);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled]);

  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      reset: () => {
        captchaRef.current?.resetCaptcha();
        onToken(null);
      },
      focus: () => boxRef.current?.focus(),
    };
    return () => {
      if (handleRef) handleRef.current = null;
    };
  }, [handleRef, onToken]);

  if (!enabled) return null;
  const key = siteKey || TEST_SITE_KEY;

  return (
    <div
      ref={boxRef}
      tabIndex={-1}
      aria-labelledby={labelId}
      className="min-h-[78px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ minHeight: compact ? 144 : 78 }}
    >
      <span id={labelId} className="sr-only">hCaptcha</span>
      <HCaptcha
        ref={captchaRef}
        sitekey={key}
        size={compact ? "compact" : "normal"}
        theme={resolvedTheme === "dark" ? "dark" : "light"}
        languageOverride={i18n.resolvedLanguage?.startsWith("ms") ? "ms" : "en"}
        onVerify={(token) => onToken(token)}
        onExpire={() => onToken(null)}
        onError={() => onToken(null)}
      />
      {error && (
        <p role="alert" className="mt-1 flex items-start gap-1.5 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {error}
        </p>
      )}
    </div>
  );
}
